import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  type FieldError,
  type PurchasableItemKind,
  type PurchasableItemRowState,
  newPurchasableItemRow,
} from './shared'

type PurchasableItemsCardProps = {
  kind: PurchasableItemKind
  title: string
  description: ReactNode
  rows: PurchasableItemRowState[]
  setAllRows: Dispatch<SetStateAction<PurchasableItemRowState[]>>
  rowLabel: string
  ctaLabel: string
  namePlaceholder: string
  descriptionPlaceholder: string
  removedNoun: string
  stripePaymentReady: boolean
  stripeBlurb: string
  fieldError: FieldError
  showAdFormat?: boolean
  allowFree?: boolean
}

export default function PurchasableItemsCard({
  kind,
  title,
  description,
  rows,
  setAllRows,
  rowLabel,
  ctaLabel,
  namePlaceholder,
  descriptionPlaceholder,
  removedNoun,
  stripePaymentReady,
  stripeBlurb,
  fieldError,
  showAdFormat = false,
  allowFree = false,
}: PurchasableItemsCardProps) {
  function updateRow(clientId: string, patch: Partial<PurchasableItemRowState>) {
    setAllRows((all) => all.map((row) => (row.client_id === clientId ? { ...row, ...patch } : row)))
  }

  function removeRow(clientId: string) {
    setAllRows((all) => {
      const target = all.find((row) => row.client_id === clientId)
      if (!target) return all
      if (target.id) return all.map((row) => (row.client_id === clientId ? { ...row, destroyed: true } : row))
      return all.filter((row) => row.client_id !== clientId)
    })
  }

  function undoRemove(clientId: string) {
    setAllRows((all) => all.map((row) => (row.client_id === clientId ? { ...row, destroyed: false } : row)))
  }

  const showEditor = stripePaymentReady || allowFree

  return (
    <Card id={kind === 'ticket' ? 'selling' : undefined}>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>{title}</CardTitle>
        {showEditor && (
          <Button type="button" variant="outline" size="sm" onClick={() => setAllRows((all) => [...all, newPurchasableItemRow(kind)])}>
            <Plus className="size-4" /> {ctaLabel}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {!showEditor ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            {stripeBlurb}{' '}
            <a href="/dashboard/settings/payments" className="font-medium underline">
              Set up payments
            </a>
            .
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{description}</p>
            {allowFree && !stripePaymentReady && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                Free tickets work without Stripe. To sell paid tickets,{' '}
                <a href="/dashboard/settings/payments" className="font-medium underline">
                  set up payments
                </a>
                .
              </div>
            )}
            <div className="space-y-3">
              {rows.map((row, index) => (
                row.destroyed ? (
                  <div
                    key={row.client_id}
                    className="flex items-center justify-between rounded-md border border-black/10 bg-muted/40 p-4 text-sm"
                  >
                    <span>{row.name || removedNoun} will be removed when you save.</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => undoRemove(row.client_id)}>
                      Undo
                    </Button>
                  </div>
                ) : (
                  <PurchasableItemRow
                    key={row.client_id}
                    row={row}
                    index={index}
                    rowLabel={rowLabel}
                    namePlaceholder={namePlaceholder}
                    descriptionPlaceholder={descriptionPlaceholder}
                    allowFree={allowFree}
                    showAdFormat={showAdFormat}
                    updateRow={updateRow}
                    removeRow={removeRow}
                    fieldError={fieldError}
                  />
                )
              ))}
            </div>
            {fieldError('purchasable_items.name') && (
              <p className="text-base text-destructive">{fieldError('purchasable_items.name')}</p>
            )}
            {fieldError('purchasable_items.price_cents') && (
              <p className="text-base text-destructive">{fieldError('purchasable_items.price_cents')}</p>
            )}
            {fieldError('purchasable_items.price_dollars') && (
              <p className="text-base text-destructive">{fieldError('purchasable_items.price_dollars')}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function PurchasableItemRow({
  row,
  index,
  rowLabel,
  namePlaceholder,
  descriptionPlaceholder,
  allowFree,
  showAdFormat,
  updateRow,
  removeRow,
  fieldError,
}: {
  row: PurchasableItemRowState
  index: number
  rowLabel: string
  namePlaceholder: string
  descriptionPlaceholder: string
  allowFree: boolean
  showAdFormat: boolean
  updateRow: (clientId: string, patch: Partial<PurchasableItemRowState>) => void
  removeRow: (clientId: string) => void
  fieldError: FieldError
}) {
  const free = allowFree && row.price_dollars !== '' && parseFloat(row.price_dollars || '0') === 0

  return (
    <div className="space-y-3 rounded-md border border-black/10 bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {rowLabel} {index + 1}
          {free && (
            <span className="ml-2 inline-flex items-center rounded-full bg-secondary/30 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-foreground">
              Free
            </span>
          )}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(row.client_id)}>
          <Trash2 className="size-4" />
          <span className="sr-only">Remove {rowLabel.toLowerCase()}</span>
        </Button>
      </div>
      {row.id && fieldError('base', row.id) && (
        <p className="text-xs text-destructive">{fieldError('base', row.id)}</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`pi_name_${row.client_id}`}>Name *</Label>
          <Input
            id={`pi_name_${row.client_id}`}
            placeholder={namePlaceholder}
            value={row.name}
            onChange={(event) => updateRow(row.client_id, { name: event.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`pi_price_${row.client_id}`}>Price (USD) *</Label>
          <Input
            id={`pi_price_${row.client_id}`}
            type="number"
            step="0.01"
            min={allowFree ? '0' : '0.01'}
            placeholder={allowFree ? '0.00' : '50.00'}
            value={row.price_dollars}
            onChange={(event) => updateRow(row.client_id, { price_dollars: event.target.value })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`pi_description_${row.client_id}`}>Description</Label>
        <Textarea
          id={`pi_description_${row.client_id}`}
          rows={3}
          placeholder={descriptionPlaceholder}
          value={row.description}
          onChange={(event) => updateRow(row.client_id, { description: event.target.value })}
        />
      </div>
      <div className={`grid gap-3 ${row.kind === 'ad' ? 'sm:grid-cols-3' : 'sm:grid-cols-4'}`}>
        {row.kind !== 'ad' && (
          <div className="space-y-2">
            <Label htmlFor={`pi_tickets_${row.client_id}`}>Tickets</Label>
            <Input
              id={`pi_tickets_${row.client_id}`}
              type="number"
              min="1"
              max="20"
              placeholder="1"
              value={row.tickets_per_purchase}
              disabled={row.sold}
              onChange={(event) => updateRow(row.client_id, { tickets_per_purchase: event.target.value })}
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor={`pi_inventory_${row.client_id}`}>Inventory</Label>
          <Input
            id={`pi_inventory_${row.client_id}`}
            type="number"
            min="1"
            placeholder="Unlimited"
            value={row.inventory_limit}
            onChange={(event) => updateRow(row.client_id, { inventory_limit: event.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`pi_max_${row.client_id}`}>Max per guest</Label>
          <Input
            id={`pi_max_${row.client_id}`}
            type="number"
            min="1"
            max="20"
            placeholder="No limit"
            value={row.max_per_buyer}
            onChange={(event) => updateRow(row.client_id, { max_per_buyer: event.target.value })}
          />
        </div>
        {showAdFormat && (
          <div className="space-y-2">
            <Label htmlFor={`pi_format_${row.client_id}`}>Format</Label>
            <Select value={row.ad_format} onValueChange={(value) => updateRow(row.client_id, { ad_format: value as 'print' | 'digital' })}>
              <SelectTrigger id={`pi_format_${row.client_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="print">Print</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Switch
          id={`pi_active_${row.client_id}`}
          checked={row.active}
          onCheckedChange={(checked) => updateRow(row.client_id, { active: checked })}
        />
        <Label htmlFor={`pi_active_${row.client_id}`}>Active</Label>
      </div>
    </div>
  )
}
